import type { IPublicClientApplication } from '@azure/msal-browser'

import { consumeSignInResponse, forgetHopTabSignIn } from '@/shared/auth/msal-instance'
import {
  abandonPendingHop,
  clearHopToken,
  consumeHopCode,
  forgetHopStart,
  holdHopSignIn,
  hopFailureAddress,
  isSignedInThroughHop,
  loadHopToken,
  readPendingHop,
  returnFromHop,
  type HopResult,
} from '@/shared/auth/sign-in-hop'
import type { RuntimeConfig } from '@/shared/config/runtime-config'

import { describeOtherError, describeSignInFailure, settleSignInFailure, type SignInFailure } from './sign-in-failure'

/**
 * Before first render: takes the sign-in answer in the address, B2C's or a hop's, and settles the
 * failure the callback shows, a new one or the one kept for a reload. A failed sign-in is ordinary
 * use: the app shows it at the callback, in the site's own frame. `null` when the tab is leaving,
 * back to the site whose hop B2C signed nobody in for.
 */
export async function takeSignInAnswer(
  config: RuntimeConfig,
  msalInstance: IPublicClientApplication | null,
  hopResult: HopResult | null,
): Promise<{ readonly failure: SignInFailure | undefined } | null> {
  let failure: SignInFailure | undefined
  // Any answer, a hop's or B2C's, replaces a failure kept for a reload of the callback.
  let isAnswered = hopResult !== null
  const isHopSite = isSignedInThroughHop(config.auth)
  try {
    if (isHopSite) {
      await loadHopToken()
      if (hopResult !== null && 'failure' in hopResult) {
        // The sign-in site sent the hop back without a code: shown here, in this site's own frame.
        forgetHopStart()
        failure = { kind: hopResult.failure, details: 'The sign-in through the sign-in site did not finish.' }
      } else {
        await consumeHopCode(config.apiBaseUrl, hopResult?.code ?? null)
      }
    } else {
      // A sign-in link for this site (SM-39) holds its token as a hop site does; the next B2C
      // sign-in here drops it.
      await loadHopToken()
      if (hopResult !== null && 'failure' in hopResult) {
        forgetHopStart()
        failure = { kind: hopResult.failure, details: 'The sign-in link could not be used.' }
      } else if (hopResult !== null && (await consumeHopCode(config.apiBaseUrl, hopResult.code)) === 'signedIn') {
        // Nobody else stays signed in in this browser behind the link's person.
        await msalInstance?.clearCache()
      }

      // Another site's hop coming back: its sign-in is kept for the callback, never made active here.
      const isHop = readPendingHop() !== null
      const result = await consumeSignInResponse(msalInstance, isHop)
      isAnswered ||= result !== null
      if (isHop && result !== null) {
        holdHopSignIn({ accessToken: result.accessToken })
      } else if (result !== null) {
        await clearHopToken()
      }
    }
  } catch (error: unknown) {
    if (isHopSite) {
      // A hop's code that was used, expired or never this tab's: starting again works.
      failure = { kind: 'interrupted', details: describeOtherError(error) }
    } else {
      // B2C signed nobody in for another site's hop, as when the person canceled: it is forgotten,
      // and the person goes back to the hop's site, which says why; this site shows no page of its own.
      const pending = readPendingHop()
      abandonPendingHop()
      failure = describeSignInFailure(error)
      if (pending !== null) {
        // This tab keeps nothing of the hop: the sign-in site never keeps a sign-in of its own.
        await forgetHopTabSignIn(msalInstance)
        returnFromHop(hopFailureAddress(pending.returnTo, config.auth.redirectPath, failure.kind))
        return null
      }
    }
  }
  return { failure: settleSignInFailure(failure, config.auth.redirectPath, isAnswered) }
}
